/**
 * What the web remembers about its own Queue entries, per computer (epic
 * PI-SPEC-SW-E289, feature PI-SPEC-SW-1474, UX-PM-26).
 *
 * The bridge call `enqueueAuthorQuestions(project, itemId)` carries no title,
 * and ER-1636 rules out a Toolbox contract change, so the page that queues an
 * entry remembers the item title it already shows. The Queue screen reads it
 * back when the app reports the entry with `title: null`. The queue itself is
 * per computer, so localStorage is the right scope.
 *
 * It also remembers when this computer posted an item's author questions, so
 * a finished author-questions entry can say "posted" instead of "not posted"
 * (posting is otherwise only known to the card that posted, UX-PM-06).
 *
 * Both are small capped lists (newest last, at most {@link QUEUE_TITLES_CAP}
 * entries; the oldest drop off). Storage errors (private mode, quota, bad
 * JSON) are swallowed: a missing title only means the Queue falls back to
 * cached reads.
 *
 * Keys are queue entry keys from the contract, `queueEntryKey(kind, project, itemId)`.
 */

/** localStorage key of the remembered titles. */
export const QUEUE_TITLES_STORAGE_KEY = "spectree.grooming.queueTitles";

/** localStorage key of when this computer posted an item's author questions. */
export const QUEUE_QUESTIONS_POSTED_STORAGE_KEY = "spectree.grooming.queueQuestionsPosted";

/** How many entries each list keeps. */
export const QUEUE_TITLES_CAP = 100;

type Pairs = [string, string][];

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readPairs(storageKey: string): Pairs {
  try {
    const raw = storage()?.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (pair): pair is [string, string] =>
        Array.isArray(pair) && pair.length === 2 && typeof pair[0] === "string" && typeof pair[1] === "string"
    );
  } catch {
    return [];
  }
}

function writePair(storageKey: string, key: string, value: string): void {
  try {
    const pairs = readPairs(storageKey).filter(([existing]) => existing !== key);
    pairs.push([key, value]);
    storage()?.setItem(storageKey, JSON.stringify(pairs.slice(-QUEUE_TITLES_CAP)));
  } catch {
    // A title we cannot store only means the Queue falls back to cached reads.
  }
}

function readValue(storageKey: string, key: string): string | null {
  const pair = readPairs(storageKey).find(([existing]) => existing === key);
  return pair ? pair[1] : null;
}

/** Remember the title of the item an entry was queued for. Blank titles are ignored. */
export function rememberQueueTitle(key: string, title: string): void {
  const trimmed = title.trim();
  if (key.length === 0 || trimmed.length === 0) return;
  writePair(QUEUE_TITLES_STORAGE_KEY, key, trimmed);
}

/** The title remembered for an entry key, or null. */
export function queueTitle(key: string): string | null {
  return readValue(QUEUE_TITLES_STORAGE_KEY, key);
}

/** Remember that this computer posted the author questions of the entry key, now (or `at`, ISO 8601). */
export function rememberQuestionsPosted(key: string, at: string = new Date().toISOString()): void {
  if (key.length === 0) return;
  writePair(QUEUE_QUESTIONS_POSTED_STORAGE_KEY, key, at);
}

/** When this computer last posted the author questions of the entry key (ISO 8601), or null. */
export function questionsPostedAt(key: string): string | null {
  return readValue(QUEUE_QUESTIONS_POSTED_STORAGE_KEY, key);
}
